import type {
  AgendaItemCompletedBy,
  AgendaItemStatus,
  AgendaItemType,
} from "@elation/api-client/src/stitch-web";
import { Text } from "@src/components/general/Text";
import { HorizontalFlexbox } from "@src/components/layout/Flexbox";
import { Button } from "@src/components/ui/button";
import { cn } from "@src/lib/utils";
import { Check, FileText, X } from "lucide-react";

export interface AgendaItemProps {
  type: AgendaItemType;
  description: string;
  status: AgendaItemStatus;
  completedBy?: AgendaItemCompletedBy | null;
  onToggleComplete: () => void;
  onRemove: () => void;
  isPending?: boolean;
}

export const AgendaItem = ({
  description,
  status,
  completedBy,
  onToggleComplete,
  onRemove,
  isPending = false,
}: AgendaItemProps) => {
  const completed = status === "finalized" || status === "was_discussed";

  const completedSuffix = completed
    ? completedBy === "ai_agent"
      ? " — completed by AI"
      : " — completed"
    : "";

  return (
    <li
      data-testid="agenda-item"
      aria-label={`${description}${completedSuffix}`}
      className="list-none"
    >
      <HorizontalFlexbox
        className={cn(
          "items-stretch overflow-hidden rounded-lg border border-border bg-card shadow-soft",
          completed && "opacity-70",
          isPending && "opacity-60",
        )}
      >
        <div
          aria-hidden
          className={cn("w-1 shrink-0 bg-accent/40", completed && "bg-success")}
        />
        <HorizontalFlexbox
          alignItems="center"
          justifyContent="space-between"
          gap="sm"
          className="flex-1 px-4 py-3"
        >
          <HorizontalFlexbox
            alignItems="center"
            gap="xs"
            className="min-w-0 flex-1"
          >
            <FileText className="size-4 shrink-0 text-accent" />
            <Text
              sizing="sm"
              className={cn("line-clamp-2", completed && "line-through")}
            >
              {description}
            </Text>
          </HorizontalFlexbox>
          <HorizontalFlexbox alignItems="center" gap="xs" className="shrink-0">
            {completed && completedBy === "ai_agent" ? (
              <span
                aria-label="Completed by AI"
                className="rounded-md bg-accent/15 px-1.5 py-0.5 text-[10px] font-semibold text-accent"
              >
                AI
              </span>
            ) : null}
            {!isPending && (
              <button
                type="button"
                role="checkbox"
                aria-checked={completed}
                onClick={onToggleComplete}
                aria-label={completed ? "Mark incomplete" : "Mark complete"}
                className={cn(
                  "flex size-6 items-center justify-center rounded-full border transition-colors",
                  completed
                    ? "border-success bg-success text-success-foreground"
                    : "border-border text-transparent hover:border-accent",
                )}
              >
                <Check className="size-3.5" strokeWidth={3} />
              </button>
            )}
            {!isPending && onRemove ? (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Remove"
                onClick={onRemove}
                className="size-8"
              >
                <X />
              </Button>
            ) : null}
          </HorizontalFlexbox>
        </HorizontalFlexbox>
      </HorizontalFlexbox>
    </li>
  );
};
